import React, { useState, useEffect, useRef } from 'react'
import { Alert, Card, CardDeck, Badge, Form, Button } from 'react-bootstrap'
import Title from 'common/components/Title'
import MonthOfYear from 'common/logic/MonthOfYear'
import API from 'common/logic/API'
import BigSpinner from 'common/components/BigSpinner'
import * as Icons from 'react-bootstrap-icons'
import { Bar, getElementAtEvent } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, LineController, PointElement, Title as ChartTitle, Tooltip, Legend } from 'chart.js'
import Colors from 'common/utils/Colors'
import MonthPicker from 'common/components/MonthPicker'
import Utils from 'common/Utils'
import { useNavigate } from 'react-router-dom'
import _ from 'lodash'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, LineController, PointElement, ChartTitle, Tooltip, Legend)

interface TrendMonth {
    actual: Record<string, string>
    expected: Record<string, string>
    month: string
    planId: number
}

interface TrendData {
    months: TrendMonth[]
    actualTotal: Record<string, string>
    expectedTotal: Record<string, string>
}

function Trends() {
    const [startMonth, setStartMonth] = useState<MonthOfYear | null>(null)
    const [endMonth, setEndMonth] = useState<MonthOfYear | null>(null)
    const [trend, setTrend] = useState<TrendData | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [modal, setModal] = useState<{ type: string } | null>(null)

    const calculateMonth = () => {
        const query = new URLSearchParams(window.location.search)
        const startParam = query.get("start") || ""
        const endParam = query.get("end") || ""
        const startM = MonthOfYear.parse(startParam)
        const endM = MonthOfYear.parse(endParam)
        const now = MonthOfYear.now()

        let range: { start: MonthOfYear; end: MonthOfYear }
        if (startM && endM) {
            range = { start: startM, end: endM }
        } else if (startM) {
            range = { start: startM, end: startM.nextYear() }
        } else if (endM) {
            range = { start: endM.previousYear(), end: endM }
        } else {
            range = { start: now.previousYear(), end: now }
        }

        range.start = range.start.min(now)
        range.end = range.end.min(now)
        range.end = range.end.max(range.start)
        return range
    }

    const fetchData = (start: MonthOfYear, end: MonthOfYear) => {
        setTrend(null)
        API.multiple({
            trend: API.get(`/expense-report/trend?start=${start.toString()}&end=${end.toString()}`)
        }).then((response: any) => {
            setTrend(response.trend as TrendData)
        }).catch((err: any) => {
            setError(err.message)
        })
    }

    useEffect(() => {
        const range = calculateMonth()
        setStartMonth(range.start)
        setEndMonth(range.end)
        fetchData(range.start, range.end)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const hideDialogs = () => setModal(null)
    const showMonthSelection = () => setModal({ type: "picker" })

    const onSelectMonths = (months: MonthOfYear | MonthOfYear[]) => {
        hideDialogs()
        const monthsArr = Array.isArray(months) ? months : [months]
        Utils.pushUrlWithParams({
            start: monthsArr[0].toString(),
            end: monthsArr[1].toString()
        })
        setStartMonth(monthsArr[0])
        setEndMonth(monthsArr[1])
        fetchData(monthsArr[0], monthsArr[1])
    }

    if (error) {
        return <><Title title="Trend" /><Alert variant="danger">{error}</Alert></>
    }

    if (!trend || !startMonth || !endMonth) {
        return <><Title title="Trend" /><BigSpinner /></>
    }

    const howManyMonths = trend.months.length
    const firstMonth = trend.months[0].month
    const lastMonth = trend.months[howManyMonths - 1].month
    const currencies = _.keys(trend.actualTotal)

    const nextMonths: [MonthOfYear, MonthOfYear] = [startMonth.nextMonth(), endMonth.nextMonth()]
    const prevMonths: [MonthOfYear, MonthOfYear] = [startMonth.previousMonth(), endMonth.previousMonth()]

    let modalElement: React.ReactNode = null
    if (modal) {
        switch (modal.type) {
            case "picker":
                modalElement = <MonthPicker
                    show={true}
                    selectRange={true}
                    onClose={hideDialogs}
                    onSelect={onSelectMonths}
                />
                break
            default:
        }
    }

    return <>
        <Title title="Income trend" />
        <p>
            <Button
            onClick={() => onSelectMonths(prevMonths)}
            variant="link"
            className="prev"><Icons.ChevronDoubleLeft /></Button>

        From {firstMonth} to {lastMonth}{' '}
        <Button variant="link" onClick={showMonthSelection}>
            <Icons.BoxArrowInUpRight />
        </Button>

        <Button
            onClick={() => onSelectMonths(nextMonths)}
            variant="link"
            className="next"><Icons.ChevronDoubleRight /></Button>

        {currencies.length > 1 ?
            <>Jump to {currencies.map(c =>
                <span className="inline-link-list" key={c}><a href={`#currency_${c}`}>{c}</a></span>)}</>
            : ''}
        </p>
        {modalElement}
        {currencies.map((c) =>
            <TrendsForCurrency
                id={`currency_${c}`}
                needsTitle={currencies.length > 1}
                key={c}
                currency={c}
                actualTotal={trend.actualTotal[c]}
                expectedTotal={trend.expectedTotal[c]}
                months={trend.months}
            />)}
    </>
}

interface TrendsForCurrencyProps {
    id: string
    needsTitle: boolean
    currency: string
    actualTotal: string
    expectedTotal: string
    months: TrendMonth[]
}

function TrendsForCurrency({ id, needsTitle, currency, actualTotal, expectedTotal, months }: TrendsForCurrencyProps) {
    const [showCumulative, setShowCumulative] = useState(false)
    const [hideEmpty, setHideEmpty] = useState(true)
    const navigate = useNavigate()
    const chartRef = useRef<ChartJS<'bar'> | null>(null)

    const monthsData = months.map((m) => ({
        actual: parseFloat(m.actual[currency]) || 0,
        expected: parseFloat(m.expected[currency]) || 0,
        month: m.month
    }))

    const firstNonEmptyMonth = monthsData.findIndex(m => m.actual !== 0 || m.expected !== 0)
    const nonEmptyMonthsData = firstNonEmptyMonth > -1 ? monthsData.slice(firstNonEmptyMonth) : monthsData

    let cumulativeSum = 0.0
    const cumulativeAmounts = monthsData.map(m => {
        cumulativeSum += parseFloat(String(m.actual))
        return cumulativeSum
    })

    cumulativeSum = 0.0
    const nonEmptyCumulativeAmounts = nonEmptyMonthsData.map(m => {
        cumulativeSum += parseFloat(String(m.actual))
        return cumulativeSum
    })

    const actualTotalNum = parseFloat(actualTotal)
    const expectedTotalNum = parseFloat(expectedTotal)
    const diff = actualTotalNum - expectedTotalNum
    const lessThanExpected = diff < 0
    let percentage: string | undefined = parseFloat(expectedTotal) !== 0
        ? ((diff / parseFloat(expectedTotal)) * 100).toFixed(1)
        : undefined
    const trendIcon = lessThanExpected ? <Icons.CaretDownFill /> : <Icons.CaretUpFill />

    if (percentage !== undefined && parseFloat(percentage) < 0 && parseFloat(expectedTotal) < 0) {
        percentage = String(-parseFloat(percentage))
    }

    const data = hideEmpty ? nonEmptyMonthsData : monthsData

    const onChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
        const chart = chartRef.current
        if (!chart) {
            return
        }

        const elements = getElementAtEvent(chart, event)
        if (!elements.length) {
            return
        }

        const month = data[elements[0].index]?.month
        if (month) {
            navigate(`/report?month=${month}`)
        }
    }

    const datasets: any[] = [
        {
            data: data.map((m) => m.actual),
            backgroundColor: data.map((m) => Colors.redOrangeByThreshold(0, m.expected, m.actual, 0.3)),
            borderColor: data.map((m) => Colors.redOrangeByThreshold(0, m.expected, m.actual)),
            borderWidth: 1,
            label: "Actual amount"
        },
        {
            data: data.map((m) => m.expected),
            borderWidth: 1,
            label: "Expected"
        }
    ]

    if (showCumulative) {
        datasets.push({
            data: hideEmpty ? nonEmptyCumulativeAmounts : cumulativeAmounts,
            type: "line",
            label: "Cumulative amount",
            backgroundColor: Colors.somberBlue(0.5),
            borderColor: Colors.somberBlue(0.5),
            fill: false,
            tension: 0.1
        })
    }

    return <>
        {needsTitle ? <h2 id={id}>Currency: {currency}</h2> : null}
        <CardDeck>
            <Card>
                <Card.Body>
                    <Card.Title><Icons.CurrencyExchange /> Actual amount</Card.Title>
                    <Card.Text className="card-big-amount">
                        {Utils.formatMoney(actualTotalNum)} {currency}{' '}
                        <Badge variant={lessThanExpected ? "danger" : "success"}>{trendIcon}{' '}
                            {percentage !== undefined ? `${percentage} %` : ''} </Badge>
                    </Card.Text>
                </Card.Body>
            </Card>
            <Card>
                <Card.Body>
                    <Card.Title><Icons.Easel /> Expected amount</Card.Title>
                    <Card.Text className="card-big-amount">
                        {Utils.formatMoney(expectedTotalNum)} {currency}
                    </Card.Text>
                </Card.Body>
            </Card>
        </CardDeck>
        <Card>
            <Card.Body>
                <Form.Check
                    inline
                    type="checkbox"
                    label="Show cumulative amount"
                    checked={showCumulative}
                    onChange={() => setShowCumulative(v => !v)} />
                <Form.Check
                    inline
                    type="checkbox"
                    label="Hide empty months"
                    checked={hideEmpty}
                    onChange={() => setHideEmpty(v => !v)} />
                <Bar
                    ref={chartRef}
                    onClick={onChartClick}
                    data={{
                        datasets: datasets,
                        labels: data.map(m => m.month)
                    }}
                />
            </Card.Body>
        </Card>
    </>
}

export default Trends
